import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../auth/workosAuth";
import { Box, Tooltip, IconButton } from "@mui/material";
import { ChevronLeft, ChevronRight } from "react-feather";
import { useEditorV2 } from "../../contexts/EditorV2Context";
import { useGlobalChat } from "../../contexts/GlobalChatContext";

const EditorFloatingDrawer = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [editorVersion, setEditorVersion] = useState(
    localStorage.getItem("sqlEditorVersion") || "v2"
  );
  const drawerRef = useRef(null);

  // Vertical position state (offset from bottom, stored in localStorage)
  const [bottomOffset, setBottomOffset] = useState(() => {
    const saved = localStorage.getItem("deployDrawerBottomOffset");
    return saved ? parseInt(saved, 10) : null; // null means use default
  });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartY = useRef(0);
  const dragStartOffset = useRef(0);
  const justFinishedDragging = useRef(false);

  const isOnSqlEditor = location.pathname === "/sql/editor";
  const isOnCodePage = location.pathname === "/code";

  // V2 Editor context
  const editorV2Context = useEditorV2();
  const activeVSCode = editorV2Context?.activeVSCode;
  const setActiveVSCode = editorV2Context?.setActiveVSCode;
  const isDeploying = editorV2Context?.isDeploying;
  const setIsDeploying = editorV2Context?.setIsDeploying;
  const setIsScheduleModalShown = editorV2Context?.setIsScheduleModalShown;
  const setIsPullRequestModalV2Shown = editorV2Context?.setIsPullRequestModalV2Shown;
  const triggerSandboxRefresh = editorV2Context?.triggerSandboxRefresh;

  // Global chat context for Robin button
  const { showChatbot } = useGlobalChat();

  // Environment-specific controls only show when there's an active environment
  const showEnvironmentControls = activeVSCode || isDeploying;
  const isDarkMode = !!activeVSCode;

  // Listen for storage changes
  useEffect(() => {
    const handleStorageChange = () => {
      const version = localStorage.getItem("sqlEditorVersion") || "v2";
      setEditorVersion(version);
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Collapse drawer when navigating to Code page or when environment changes
  useEffect(() => {
    if (isOnCodePage) {
      setIsExpanded(false);
    }
  }, [isOnCodePage, activeVSCode]);

  // Click outside to close drawer
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (drawerRef.current && !drawerRef.current.contains(event.target) && isExpanded) {
        setIsExpanded(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isExpanded]);

  // Drag handlers for vertical movement - only start drag if mouse moves > 3px
  const hasDragged = useRef(false);

  const handleDragStart = (e) => {
    // Don't preventDefault - allow clicks to work
    dragStartY.current = e.clientY;
    const defaultBottom = activeVSCode ? 0 : 60;
    dragStartOffset.current = bottomOffset !== null ? bottomOffset : defaultBottom;
    hasDragged.current = false;

    const handleDragMove = (moveEvent) => {
      const deltaY = dragStartY.current - moveEvent.clientY;

      // Only start actual drag if moved more than 3px
      if (Math.abs(deltaY) > 3) {
        hasDragged.current = true;
        setIsDragging(true);
        document.body.style.cursor = "grabbing";
        document.body.style.userSelect = "none";
        const newOffset = Math.max(0, Math.min(window.innerHeight - 100, dragStartOffset.current + deltaY));
        setBottomOffset(newOffset);
      }
    };

    const handleDragEnd = () => {
      document.removeEventListener("mousemove", handleDragMove);
      document.removeEventListener("mouseup", handleDragEnd);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";

      if (hasDragged.current) {
        setIsDragging(false);
        localStorage.setItem("deployDrawerBottomOffset", (bottomOffset ?? dragStartOffset.current).toString());
        justFinishedDragging.current = true;
        setTimeout(() => {
          justFinishedDragging.current = false;
        }, 100);
      }
    };

    document.addEventListener("mousemove", handleDragMove);
    document.addEventListener("mouseup", handleDragEnd);
  };

  // Calculate the actual bottom position
  const defaultBottom = activeVSCode ? 0 : 60;
  const actualBottom = bottomOffset !== null ? bottomOffset : defaultBottom;

  // Don't render if using Editor v1 (except on /code page where v1/v2 doesn't apply)
  // Show on SQL editor always, or on Code page always
  if (!isOnSqlEditor && !isOnCodePage) {
    return null;
  }
  if (isOnSqlEditor && editorVersion !== "v2") {
    return null;
  }

  const tooltipProps = {
    arrow: true,
    placement: "left",
    slotProps: {
      arrow: { sx: { color: "#546e7a" } },
      tooltip: {
        sx: {
          backgroundColor: "#546e7a",
          fontSize: "11px",
          padding: "6px 12px",
          borderRadius: "6px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        },
      },
    },
  };

  const buttonStyle = {
    padding: "6px 10px",
    backgroundColor: "#2c49fc",
    color: "#ffffff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "11px",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: "6px",
    transition: "all 0.15s ease-in-out",
    width: "100%",
  };

  return (
    <>
      {/* Global overlay to prevent iframe from capturing mouse events during drag */}
      {isDragging && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999,
            cursor: "grabbing",
          }}
        />
      )}
      <Box
        ref={drawerRef}
        sx={{
          position: "fixed",
          right: 0,
          bottom: actualBottom,
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          transition: isDragging ? "none" : "bottom 0.2s ease-out",
        }}
      >
      {/* Drag Handle - vertical bar for repositioning */}
      <Box
        onMouseDown={handleDragStart}
        sx={{
          width: "4px",
          height: "36px",
          backgroundColor: isDragging ? "#3266bb" : "#dee2e6",
          borderRadius: "4px 0 0 4px",
          cursor: isDragging ? "grabbing" : "grab",
          transition: "background-color 0.15s ease",
          "&:hover": {
            backgroundColor: "#adb5bd",
          },
        }}
      />
      {/* Toggle Handle - also draggable (always light theme) */}
      <IconButton
        onMouseDown={handleDragStart}
        onClick={() => {
          // Ignore clicks that immediately follow a drag
          if (justFinishedDragging.current) return;
          setIsExpanded((prev) => !prev);
        }}
        sx={{
          backgroundColor: "#fff",
          borderRadius: "0",
          boxShadow: "-2px 0 8px rgba(0,0,0,0.1)",
          width: "auto",
          height: 36,
          paddingX: 1,
          cursor: isDragging ? "grabbing" : "grab",
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          "&:hover": {
            backgroundColor: "#f8f9fa",
          },
        }}
      >
        {!isExpanded && <span style={{ fontSize: "11px", fontWeight: 500, color: "#495057" }}>Deploy</span>}
        {isExpanded ? (
          <ChevronRight size={16} color="#495057" />
        ) : (
          <ChevronLeft size="#495057" />
        )}
      </IconButton>

      {/* Drawer Content - only render content when expanded to prevent stuck tooltips */}
      {isExpanded && (
      <Box
        sx={{
          backgroundColor: isDarkMode ? "#252526" : "#fff",
          boxShadow: isDarkMode ? "-4px 0 20px rgba(0,0,0,0.3)" : "-4px 0 20px rgba(0,0,0,0.1)",
          borderRadius: "8px 0 0 8px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          padding: "12px",
          minWidth: "140px",
        }}
      >
        {/* Environment-specific controls - only show when there's an active environment */}
        {showEnvironmentControls && (
          <>
            {/* Back to Environments */}
            <Tooltip title="Back to Environments" {...tooltipProps}>
              <button
                onClick={() => {
                  window.location.href = '/code';
                }}
                style={{
                  ...buttonStyle,
                  backgroundColor: "transparent",
                  color: isDarkMode ? "#cccccc" : "#495057",
                  border: isDarkMode ? "1px solid #3c3c3c" : "1px solid #dee2e6",
                }}
              >
                <span style={{ fontSize: "14px" }}>←</span>
                <span>Environments</span>
              </button>
            </Tooltip>
          </>
        )}

        {/* Create Pull Request */}
        <button
          onClick={() => setIsPullRequestModalV2Shown(true)}
          style={buttonStyle}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" width={14} height={14}>
            <circle cx="18" cy="18" r="3" />
            <circle cx="6" cy="6" r="3" />
            <path d="M13 6h3a2 2 0 0 1 2 2v7" />
            <line x1="6" y1="9" x2="6" y2="21" />
          </svg>
          <span>Create PR</span>
        </button>

        {/* Create DAG */}
        <button
          onClick={() => setIsScheduleModalShown(true)}
          style={buttonStyle}
        >
          <svg viewBox="0 0 512 512" fill="#fff" width={14} height={14}>
            <path d="M505.12019,19.09375c-1.18945-5.53125-6.65819-11-12.207-12.1875C460.716,0,435.507,0,410.40747,0,307.17523,0,245.26909,55.20312,199.05238,128H94.83772c-16.34763.01562-35.55658,11.875-42.88664,26.48438L2.51562,253.29688A28.4,28.4,0,0,0,0,264a24.00867,24.00867,0,0,0,24.00582,24H127.81618l-22.47457,22.46875c-11.36521,11.36133-12.99607,32.25781,0,45.25L156.24582,406.625c11.15623,11.1875,32.15619,13.15625,45.27726,0l22.47457-22.46875V488a24.00867,24.00867,0,0,0,24.00581,24,28.55934,28.55934,0,0,0,10.707-2.51562l98.72834-49.39063c14.62888-7.29687,26.50776-26.5,26.50776-42.85937V312.79688c72.59753-46.3125,128.03493-108.40626,128.03493-211.09376C512.07526,76.5,512.07526,51.29688,505.12019,19.09375ZM384.04033,168A40,40,0,1,1,424.05,128,40.02322,40.02322,0,0,1,384.04033,168Z" />
          </svg>
          <span>Create DAG</span>
        </button>

        {/* Robin AI */}
        <button onClick={() => showChatbot()} style={buttonStyle}>
          <svg viewBox="0 0 640 512" fill="#fff" width={14} height={14}>
            <path d="M320 0c17.7 0 32 14.3 32 32V96H472c39.8 0 72 32.2 72 72V440c0 39.8-32.2 72-72 72H168c-39.8 0-72-32.2-72-72V168c0-39.8 32.2-72 72-72H288V32c0-17.7 14.3-32 32-32zM208 384c-8.8 0-16 7.2-16 16s7.2 16 16 16h32c8.8 0 16-7.2 16-16s-7.2-16-16-16H208zm96 0c-8.8 0-16 7.2-16 16s7.2 16 16 16h32c8.8 0 16-7.2 16-16s-7.2-16-16-16H304zm96 0c-8.8 0-16 7.2-16 16s7.2 16 16 16h32c8.8 0 16-7.2 16-16s-7.2-16-16-16H400zM264 256a40 40 0 1 0 -80 0 40 40 0 1 0 80 0zm152 40a40 40 0 1 0 0-80 40 40 0 1 0 0 80z" />
          </svg>
          <span>Robin</span>
        </button>
      </Box>
      )}
      </Box>
    </>
  );
};

export default EditorFloatingDrawer;
